// ─── Tenant resolution by custom-domain hostname (authoritative) ────────────
// On a tenant's own custom domain (e.g. sebex.cl) the HOSTNAME identifies the
// seminary — not a query param (lost on SPA navigation / login redirect) nor
// localStorage (wrong for users in multiple seminaries). This resolves the
// hostname to {tenantId, type} via the same resolveDomain Cloud Function the
// Cloudflare Worker uses. The hostname resolution is the SOLE authority for
// tenant scoping (any injected hint is never trusted for security).
//
// FAIL-CLOSED: a custom hostname we cannot resolve (resolver down, 404, malformed)
// yields `unresolved` — the caller must DENY, never fall back to localStorage.
// Under a custom domain, fail-open = cross-tenant data leakage.

import type { DateFormat, TimeFormat } from '@dosfilos/shared';
import { resolverUrl } from './environment';

// EXACT allowlist of the platform's own hosts (tenant comes from membership, NOT
// the hostname). Matched exactly — NOT by suffix — so a NON-platform
// *.dosfilos.com (a seminary app subdomain like preach.dosfilos.com) flows to the
// resolver and resolves to that seminary (type='backoffice'). A platform host
// OMITTED here = fail-closed lockout for its users, so this list is exhaustive.
const PLATFORM_HOSTS = new Set([
  'dosfilos.com',
  'www.dosfilos.com',
  'teach.dosfilos.com', // platform marketing landing (Astro site)
  'www.teach.dosfilos.com',
  'seminary.dosfilos.com', // production app domain (APP_BASE_URL in functions' .env)
  'www.seminary.dosfilos.com',
  'dosfilos-seminary.web.app', // Firebase Hosting default for the app
  'dosfilos-seminary-prod.web.app',
  'localhost',
]);

/** True ONLY for the platform's own hosts (tenant NOT domain-derived). A
 *  non-platform *.dosfilos.com (seminary app subdomain) returns false → resolved.
 *  `.web.app` is always platform — Firebase Hosting only, covers the default host
 *  and PR preview channels (`dosfilos-seminary--<channel>.web.app`); no seminary
 *  ever uses a `.web.app` domain. NEVER a `.dosfilos.com` suffix exception — that
 *  would re-break seminary subdomain resolution. */
export function isKnownDosfilosHost(hostname: string): boolean {
  return PLATFORM_HOSTS.has(hostname) || hostname.endsWith('.web.app') || isOwnAuthHost(hostname);
}

/** `<projectId>.firebaseapp.com`: Firebase Hosting's second default host for THIS
 *  build's project (also its authDomain). Only this project's — never a
 *  `.firebaseapp.com` suffix rule. The project id is read without throwing: the
 *  app refuses to start without it (assertWebEnvironment), so here a missing id
 *  only happens in isolated tests, where it means "no such host". */
function isOwnAuthHost(hostname: string): boolean {
  const projectId = import.meta.env['VITE_FIREBASE_PROJECT_ID'] as string | undefined;
  return !!projectId && hostname === `${projectId}.firebaseapp.com`;
}

export type DomainType = 'landing' | 'backoffice';

/** Narrow an untrusted string from the resolver to a valid DateFormat. */
function isDateFormat(v: string | undefined): v is DateFormat {
  return v === 'dd-mm-yyyy' || v === 'mm-dd-yyyy' || v === 'yyyy-mm-dd';
}

/** Narrow an untrusted string from the resolver to a valid TimeFormat. */
function isTimeFormat(v: string | undefined): v is TimeFormat {
  return v === '24h' || v === '12h';
}

export type DomainResolution =
  /** A platform/base/dev host — the tenant comes from the user's membership. */
  | { mode: 'platform' }
  /** A custom domain resolved to a seminary. `timezone`/`dateFormat`/`timeFormat` =
   *  the tenant's localization for the portal. */
  | {
      mode: 'resolved';
      tenantId: string;
      slug: string;
      type: DomainType;
      timezone?: string;
      dateFormat?: DateFormat;
      timeFormat?: TimeFormat;
    }
  /** A custom host we could not resolve → caller must fail closed (deny). */
  | { mode: 'unresolved' };

/**
 * Pure classifier for the resolveDomain response — FAIL-CLOSED: anything that
 * isn't a complete, well-formed resolution becomes `unresolved`. Exported for
 * tests (the security-relevant surface is "what counts as resolved").
 */
export function classifyDomainResponse(
  ok: boolean,
  body:
    | {
        tenantId?: string;
        slug?: string;
        type?: string;
        timezone?: string;
        dateFormat?: string;
        timeFormat?: string;
      }
    | null
    | undefined,
): DomainResolution {
  if (
    ok &&
    body &&
    body.tenantId &&
    body.slug &&
    (body.type === 'landing' || body.type === 'backoffice')
  ) {
    return {
      mode: 'resolved',
      tenantId: body.tenantId,
      slug: body.slug,
      type: body.type,
      ...(body.timezone ? { timezone: body.timezone } : {}),
      ...(isDateFormat(body.dateFormat) ? { dateFormat: body.dateFormat } : {}),
      ...(isTimeFormat(body.timeFormat) ? { timeFormat: body.timeFormat } : {}),
    };
  }
  return { mode: 'unresolved' };
}

/**
 * DEV-ONLY hostname simulation, so the custom-domain flows can be exercised on
 * localhost without a real custom domain. Never active in a production build.
 *   ?__simhost=sebex.cl   → resolve as if on sebex.cl
 *   ?__simfail=1          → force `unresolved` (simulate the resolver being down)
 */
function devSim(): { host: string | null; forceFail: boolean } {
  if (!import.meta.env.DEV) return { host: null, forceFail: false };
  const p = new URLSearchParams(window.location.search);
  return { host: p.get('__simhost'), forceFail: p.get('__simfail') === '1' };
}

/** DEV-only simulated hostname (?__simhost=), so custom-domain code paths that
 *  read window.location.hostname directly can be exercised on localhost too. */
export function devSimHostname(): string | null {
  return devSim().host;
}

// Cache only deterministic outcomes (platform / resolved). A transient
// `unresolved` is NOT cached so a reload / next call can retry.
let cache: { hostname: string; result: DomainResolution } | null = null;

export async function resolveDomainTenant(): Promise<DomainResolution> {
  const sim = devSim();
  if (sim.forceFail) return { mode: 'unresolved' };
  const hostname = sim.host ?? window.location.hostname;
  if (isKnownDosfilosHost(hostname)) return { mode: 'platform' };
  if (cache?.hostname === hostname) return cache.result;

  let result: DomainResolution = { mode: 'unresolved' };
  try {
    const res = await fetch(`${resolverUrl()}?domain=${encodeURIComponent(hostname)}`, {
      signal: AbortSignal.timeout(5000), // never hang the login flow
    });
    const body = res.ok ? ((await res.json()) as Record<string, string>) : null;
    result = classifyDomainResponse(res.ok, body);
  } catch {
    return { mode: 'unresolved' }; // resolver unreachable / timeout → fail closed
  }

  if (result.mode !== 'unresolved') cache = { hostname, result };
  return result;
}
